/**
 * @file Require a valid album (via matched :albumId).
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import { AlbumData } from '../../graphql/queries/albums';

/**
 * Higher order component for the require valid album functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {React.Component} The composed component.
 */
const WithValidAlbumHOC = WrappedComponent =>
  class extends Component {
    static propTypes = {
      album: PropTypes.shape({
        id: PropTypes.string,
      }),
      loading: PropTypes.bool.isRequired,
    };

    render() {
      const { album, loading } = this.props;

      return !loading && !album ? (
        <Redirect to="/not-found" />
      ) : (
        <WrappedComponent {...this.props} />
      );
    }
  };

/**
 * Higher order container for the require valid album functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const RequireValidAlbum = WrappedComponent => {
  const WithValidAlbum = WithValidAlbumHOC(WrappedComponent);

  const withAlbumData = graphql(AlbumData, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumId,
      },
    }),
    props: ({ data: { album, loading } }) => ({
      album,
      loading,
    }),
  });

  return compose(withAlbumData)(WithValidAlbum);
};

export { WithValidAlbumHOC };
export default RequireValidAlbum;
